import React, { useState } from 'react';
import {
    resetPassword,
    confirmResetPassword,
    type ResetPasswordOutput,
    type ConfirmResetPasswordInput
} from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';


interface ForgotPasswordProps { }

interface PasswordRules {
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    length: boolean;
    specialChar: boolean;
    noLeadingTrailingSpaces: boolean;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [passwordRules, setPasswordRules] = useState<PasswordRules>({
        lowercase: false,
        uppercase: false,
        number: false,
        length: false,
        specialChar: false,
        noLeadingTrailingSpaces: false,
    });
    const [error, setError] = useState<string>('');

    const validatePassword = (password: string) => {
        const rules = {
            lowercase: /[a-z]/.test(password),
            uppercase: /[A-Z]/.test(password),
            number: /\d/.test(password),
            length: password.length >= 10,
            specialChar: (/[!@#$%^&*(),.?":{}|<>]/.test(password) || /\s/.test(password)),
            noLeadingTrailingSpaces: !/(?:^\s|\s$)/.test(password),
        };
        setPasswordRules(rules);
        return Object.values(rules).every(Boolean);
    };

    async function handleConfirmResetPassword({
        username,
        confirmationCode,
        newPassword
    }: ConfirmResetPasswordInput) {
        try {
            await confirmResetPassword({ username, confirmationCode, newPassword });
            return true;
        } catch (error: any) {
            if (error.name === 'InvalidPasswordException') {
                setError('Password does not meet requirements. Please ensure your password contains lowercase, uppercase, numbers, and special characters, is at least 10 characters long, and has no leading/trailing spaces.');
            } else if (error.name === 'CodeMismatchException') {
                setError('Invalid verification code. Please try again.');
            } else {
                setError('An error occurred while resetting your password. Please try again.');
            }
            console.error('Reset password error:', error);
            return false;
        }
    }

    const handleResetPasswordNextSteps = (output: ResetPasswordOutput) => {
        const { nextStep } = output;
        switch (nextStep.resetPasswordStep) {
            case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
                setCodeSent(true);
                break;
            case 'DONE':
                break;
        }
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (codeSent === false) {
            try {
                const output = await resetPassword({ username: email });
                handleResetPasswordNextSteps(output);
            } catch (error: any) {
                if (error.name === 'UserNotFoundException') {
                    setError('No account found with this email address.');
                } else {
                    setError('An error occurred. Please try again.');
                }
                console.error('Error during forgot password:', error);
            }
        } else {
            try {
                const success = await handleConfirmResetPassword({
                    username: email,
                    confirmationCode: code,
                    newPassword: newPassword
                });

                if (success) {
                    navigate('/login', { state: { resetSuccess: true } });
                }
            } catch (error) {
                console.error('Error during password reset:', error);
            }
        }
    };

    const getObfuscatedEmail = (email: string) => {
        const [localPart, domain] = email.split('@');
        
        // Handle edge cases for very short local parts
        if (localPart.length <= 2) {
            return `${localPart.charAt(0)}*@${domain}`;
        }
        
        const obfuscatedLocal = localPart.substring(0, 2) + '*'.repeat(localPart.length - 2);
        return `${obfuscatedLocal}@${domain}`;
    };

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src="/images/jack-logo.jpg"
                        alt="Jack Logo"
                        style={{ maxWidth: "80%", maxHeight: "30%" }}
                        className='pb-8'
                    />
                </div>
                <h2 className="text-2xl font-bold mb-6">Forgot Password</h2>
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className={codeSent ? 'hidden' : ''}>
                        <p className="mb-4">Enter your Email below and we will send a code to reset your password.</p>
                        <div className="mb-4">
                            <label htmlFor="email" className="block mb-2 font-bold text-gray-700">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                placeholder="Email"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required={!codeSent}
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-[#43a4db] text-white font-bold py-2 px-4 rounded-md hover:bg-[#2a6e95] transition duration-300"
                        >
                            Send Verification Code
                        </button>
                    </div>

                    {codeSent && (
                        <>
                            <div className="mb-6 p-4 bg-blue-50 text-blue-700 rounded-md">
                                We've sent a verification code to {getObfuscatedEmail(email)}
                            </div>
                            <div className="mb-4">
                                <label htmlFor="code" className="block mb-2 font-bold text-gray-700">
                                    Verification Code
                                </label>
                                <input
                                    type="text"
                                    id="code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="Verification Code"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div className="mb-6">
                                <label htmlFor="newPassword" className="block mb-2 font-bold text-gray-700">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    value={newPassword}
                                    autoComplete="new-password"
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        validatePassword(e.target.value);
                                    }}
                                    placeholder="New Password"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                                {/* Password requirements display */}
                                <div className="mt-2 text-sm space-y-1">
                                    <p className={passwordRules.length ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.length ? '\u2713' : '\u2717'}</span> At least 10 characters
                                    </p>
                                    <p className={passwordRules.lowercase ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.lowercase ? '\u2713' : '\u2717'}</span> One lowercase letter
                                    </p>
                                    <p className={passwordRules.uppercase ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.uppercase ? '\u2713' : '\u2717'}</span> One uppercase letter
                                    </p>
                                    <p className={passwordRules.number ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.number ? '\u2713' : '\u2717'}</span> One number
                                    </p>
                                    <p className={passwordRules.specialChar ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.specialChar ? '\u2713' : '\u2717'}</span> One special character
                                    </p>
                                    <p className={passwordRules.noLeadingTrailingSpaces ? 'text-green-600' : 'text-red-600'}>
                                        <span className="mr-1">{passwordRules.noLeadingTrailingSpaces ? '\u2713' : '\u2717'}</span> No leading or trailing spaces
                                    </p>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={!code.trim() || !Object.values(passwordRules).every(Boolean)}
                                className={`w-full font-bold py-2 px-4 rounded-md transition duration-300 ${!code.trim() || !Object.values(passwordRules).every(Boolean)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#43a4db] hover:bg-[#2a6e95] text-white'
                                    }`}
                            >
                                Reset Password
                            </button>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;